const DYMO_SDK_URL =
  'https://cdn.jsdelivr.net/gh/dymosoftware/dymo-connect-framework@2182e505e334663bf79a48dea24800f2aebcfa98/dymo.connect.framework.js'

const BADGE_NAME_OBJECT = 'BADGE_NAME'
const BADGE_ORGANISATION_OBJECT = 'BADGE_ORGANISATION'
const BADGE_INDUSTRY_OBJECT = 'BADGE_INDUSTRY'

// S0722430 / 99014: 101 mm x 54 mm; 30323 is DYMO's same-size DLS alias.
const BADGE_LABEL_XML = `<?xml version="1.0" encoding="utf-8"?>
<DieCutLabel Version="8.0" Units="twips">
  <PaperOrientation>Landscape</PaperOrientation>
  <Id>NameBadge</Id>
  <PaperName>30323 Shipping</PaperName>
  <DrawCommands>
    <RoundRectangle X="0" Y="0" Width="3061" Height="5726" Rx="120" Ry="120" />
  </DrawCommands>
  <ObjectInfo>
    <TextObject>
      <Name>${BADGE_NAME_OBJECT}</Name>
      <ForeColor Alpha="255" Red="0" Green="0" Blue="0" />
      <BackColor Alpha="0" Red="255" Green="255" Blue="255" />
      <LinkedObjectName></LinkedObjectName>
      <Rotation>Rotation0</Rotation>
      <IsMirrored>False</IsMirrored>
      <IsVariable>True</IsVariable>
      <HorizontalAlignment>Center</HorizontalAlignment>
      <VerticalAlignment>Middle</VerticalAlignment>
      <TextFitMode>ShrinkToFit</TextFitMode>
      <UseFullFontHeight>True</UseFullFontHeight>
      <Verticalized>False</Verticalized>
      <StyledText>
        <Element>
          <String>Attendee name</String>
          <Attributes>
            <Font Family="Arial" Size="26" Bold="True" Italic="False" Underline="False" Strikeout="False" />
            <ForeColor Alpha="255" Red="0" Green="0" Blue="0" />
          </Attributes>
        </Element>
      </StyledText>
    </TextObject>
    <Bounds X="300" Y="200" Width="5126" Height="900" />
  </ObjectInfo>
  <ObjectInfo>
    <TextObject>
      <Name>${BADGE_ORGANISATION_OBJECT}</Name>
      <ForeColor Alpha="255" Red="0" Green="0" Blue="0" />
      <BackColor Alpha="0" Red="255" Green="255" Blue="255" />
      <LinkedObjectName></LinkedObjectName>
      <Rotation>Rotation0</Rotation>
      <IsMirrored>False</IsMirrored>
      <IsVariable>True</IsVariable>
      <HorizontalAlignment>Center</HorizontalAlignment>
      <VerticalAlignment>Middle</VerticalAlignment>
      <TextFitMode>ShrinkToFit</TextFitMode>
      <UseFullFontHeight>True</UseFullFontHeight>
      <Verticalized>False</Verticalized>
      <StyledText>
        <Element>
          <String>Organisation</String>
          <Attributes>
            <Font Family="Arial" Size="18" Bold="True" Italic="False" Underline="False" Strikeout="False" />
            <ForeColor Alpha="255" Red="0" Green="0" Blue="0" />
          </Attributes>
        </Element>
      </StyledText>
    </TextObject>
    <Bounds X="300" Y="1200" Width="5126" Height="650" />
  </ObjectInfo>
  <ObjectInfo>
    <TextObject>
      <Name>${BADGE_INDUSTRY_OBJECT}</Name>
      <ForeColor Alpha="255" Red="0" Green="0" Blue="0" />
      <BackColor Alpha="0" Red="255" Green="255" Blue="255" />
      <LinkedObjectName></LinkedObjectName>
      <Rotation>Rotation0</Rotation>
      <IsMirrored>False</IsMirrored>
      <IsVariable>True</IsVariable>
      <HorizontalAlignment>Center</HorizontalAlignment>
      <VerticalAlignment>Middle</VerticalAlignment>
      <TextFitMode>ShrinkToFit</TextFitMode>
      <UseFullFontHeight>True</UseFullFontHeight>
      <Verticalized>False</Verticalized>
      <StyledText>
        <Element>
          <String>Industry</String>
          <Attributes>
            <Font Family="Arial" Size="12" Bold="True" Italic="False" Underline="False" Strikeout="False" />
            <ForeColor Alpha="255" Red="0" Green="0" Blue="0" />
          </Attributes>
        </Element>
      </StyledText>
    </TextObject>
    <Bounds X="300" Y="2050" Width="5126" Height="440" />
  </ObjectInfo>
</DieCutLabel>`

type DymoEnvironment = {
  isFrameworkInstalled: boolean
  errorDetails?: string
}

type DymoPrinter = {
  name: string
  isConnected: boolean
}

type DymoLabel = {
  isValidLabel: () => boolean
  setObjectText: (objectName: string, value: string) => DymoLabel
  printAsync: (printerName: string, printParamsXml?: string) => Promise<unknown>
}

type DymoFramework = {
  init: (callback: (environment: DymoEnvironment) => void) => void
  getLabelWriterPrintersAsync: () => Promise<DymoPrinter[]>
  openLabelXml: (labelXml: string) => DymoLabel
}

type DymoNamespace = {
  label: {
    framework: DymoFramework
  }
}

declare global {
  interface Window {
    dymo?: DymoNamespace
  }
}

export type BadgeData = {
  name: string
  organisation?: string
  industry?: string
}

export type BadgePrints = Record<string, string>

let dymoLoadPromise: Promise<DymoNamespace> | undefined

const normaliseFieldName = (value: string) =>
  value.toLowerCase().replace(/[^a-z0-9]/g, '')

const formatRegistrationValue = (value: unknown) => {
  if (Array.isArray(value)) return value.map(String).join(', ')
  if (value === undefined || value === null) return undefined
  if (typeof value === 'object') return undefined
  const formatted = String(value).trim()
  return formatted || undefined
}

const findRegistrationValue = (
  fields: Record<string, unknown> | undefined,
  aliases: string[]
) => {
  if (!fields) return undefined
  const match = Object.entries(fields).find(([name]) => {
    const normalisedName = normaliseFieldName(name)
    return aliases.some((alias) => normalisedName.includes(alias))
  })
  return formatRegistrationValue(match?.[1])
}

export const createBadgeData = (
  name: string,
  registrationFields?: Record<string, unknown>
): BadgeData => ({
  name: name.trim() || 'Unnamed attendee',
  organisation: findRegistrationValue(registrationFields, [
    'organisation',
    'organization',
    'company',
  ]),
  industry: findRegistrationValue(registrationFields, ['industry']),
})

const loadDymo = async () => {
  if (typeof window === 'undefined') {
    throw new Error('Badge printing is only available in a browser.')
  }
  if (window.dymo) return window.dymo
  if (dymoLoadPromise) return dymoLoadPromise

  dymoLoadPromise = new Promise<DymoNamespace>((resolve, reject) => {
    const finish = () => {
      if (window.dymo) resolve(window.dymo)
      else reject(new Error('DYMO Connect SDK did not initialise.'))
    }
    const existingScript = document.querySelector<HTMLScriptElement>(
      'script[data-forumm-dymo-sdk]'
    )

    if (existingScript) {
      existingScript.addEventListener('load', finish, { once: true })
      existingScript.addEventListener(
        'error',
        () => reject(new Error('Could not load DYMO Connect SDK.')),
        { once: true }
      )
      return
    }

    const script = document.createElement('script')
    script.src = DYMO_SDK_URL
    script.async = true
    script.dataset.forummDymoSdk = 'true'
    script.addEventListener('load', finish, { once: true })
    script.addEventListener(
      'error',
      () => reject(new Error('Could not load DYMO Connect SDK.')),
      { once: true }
    )
    document.head.appendChild(script)
  })

  try {
    return await dymoLoadPromise
  } catch (error) {
    dymoLoadPromise = undefined
    throw error
  }
}

const initialiseDymo = (framework: DymoFramework) =>
  new Promise<void>((resolve, reject) => {
    const timeout = window.setTimeout(
      () => reject(new Error('Timed out connecting to DYMO Connect.')),
      10000
    )

    try {
      framework.init((environment) => {
        window.clearTimeout(timeout)
        if (environment.isFrameworkInstalled) {
          resolve()
          return
        }
        reject(
          new Error(
            environment.errorDetails ||
              'DYMO Connect is not installed or its local service is unavailable.'
          )
        )
      })
    } catch (error) {
      window.clearTimeout(timeout)
      reject(error)
    }
  })

export const printDymoBadge = async (badge: BadgeData) => {
  const dymo = await loadDymo()
  const framework = dymo.label.framework
  await initialiseDymo(framework)

  const printers = await framework.getLabelWriterPrintersAsync()
  const printer = printers.find((candidate) => candidate.isConnected)
  if (!printer) throw new Error('No connected DYMO LabelWriter printer found.')

  const label = framework.openLabelXml(BADGE_LABEL_XML)
  if (!label.isValidLabel()) {
    throw new Error('DYMO rejected S0722430 badge label template.')
  }

  label.setObjectText(BADGE_NAME_OBJECT, badge.name)
  label.setObjectText(BADGE_ORGANISATION_OBJECT, badge.organisation || '')
  label.setObjectText(BADGE_INDUSTRY_OBJECT, badge.industry || '')

  await label.printAsync(printer.name)
  return printer.name
}

export const getBadgePrintedAt = (badgePrints: BadgePrints, email: string) =>
  badgePrints?.[email?.trim()?.toLowerCase()]
